// PersonSearchCombo - Recherche et sélection de personnes avec auto-complétion
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Search, User, UserPlus, Phone, Mail, MapPin, Loader2, X, Check } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const PersonSearchCombo = ({ 
  value, 
  onChange, 
  onPersonSelect,
  label,
  placeholder,
  required = false,
  disabled = false,
  showCreateOption = true,
  autoFillFields = [] // ['phone', 'email', 'address'] - fields to auto-fill
}) => {
  const { language } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // New person form
  const [newPerson, setNewPerson] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    national_id: '',
    create_account: false
  });

  // Debounced search
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/search?q=${encodeURIComponent(searchTerm)}&limit=10`);
        setResults(response.data || []);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (person) => {
    setSelectedPerson(person);
    setSearchTerm(person.name || `${person.first_name} ${person.last_name}`);
    setShowResults(false);
    onChange?.(person.user_id || person.id);
    onPersonSelect?.(person);
  };

  const handleClear = () => {
    setSelectedPerson(null);
    setSearchTerm('');
    onChange?.('');
    onPersonSelect?.(null);
  };

  const handleCreatePerson = async () => {
    if (!newPerson.first_name || !newPerson.last_name) {
      toast.error(language === 'fr' ? 'Nom et prénom requis' : 'First and last name required');
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/users/quick-create', {
        ...newPerson,
        name: `${newPerson.first_name} ${newPerson.last_name}`,
        role: 'CITIZEN',
        status: newPerson.create_account ? 'PENDING_ACTIVATION' : 'INACTIVE'
      });
      
      const createdPerson = response.data;
      handleSelect(createdPerson);
      setShowCreateDialog(false);
      toast.success(language === 'fr' ? 'Personne créée' : 'Person created');
      
      // Reset form
      setNewPerson({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        national_id: '',
        create_account: false
      });
    } catch (error) {
      console.error('Create error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la création' : 'Error creating person');
    } finally {
      setCreating(false);
    }
  };

  const openCreateWithSearch = () => {
    // Pre-fill with search term
    const parts = searchTerm.trim().split(' ');
    setNewPerson({
      ...newPerson,
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || ''
    });
    setShowCreateDialog(true);
  };

  return (
    <div className="relative">
      {label && (
        <Label className="mb-2 block">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      <div className="relative" ref={inputRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (selectedPerson) setSelectedPerson(null);
          }}
          onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
          placeholder={placeholder || (language === 'fr' ? 'Rechercher une personne...' : 'Search person...')}
          className={`pl-10 pr-10 ${selectedPerson ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}`}
          disabled={disabled}
          data-testid="person-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
        {selectedPerson && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {!selectedPerson && searchTerm && (
          <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-30" />
        )}
      </div>

      {/* Selected person badge */}
      {selectedPerson && (
        <div className="mt-2 flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <User className="w-3 h-3" />
            {selectedPerson.name || `${selectedPerson.first_name} ${selectedPerson.last_name}`}
          </Badge>
          {selectedPerson.phone && (
            <Badge variant="secondary" className="gap-1 text-xs">
              <Phone className="w-3 h-3" />
              {selectedPerson.phone}
            </Badge>
          )}
        </div>
      )}

      {/* Results dropdown */}
      {showResults && (results.length > 0 || searchTerm.length >= 2) && (
        <div 
          ref={resultsRef}
          className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {results.length > 0 ? (
            <>
              {results.map((person) => (
                <button
                  key={person.user_id || person.id}
                  type="button"
                  onClick={() => handleSelect(person)}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-start gap-3 border-b last:border-0"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {person.name || `${person.first_name} ${person.last_name}`}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                      {person.national_id && (
                        <span className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px]">UNJI: {person.national_id}</Badge>
                        </span>
                      )}
                      {person.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {person.phone}
                        </span>
                      )}
                      {person.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {person.email}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p className="text-sm">
                {language === 'fr' ? 'Aucun résultat trouvé' : 'No results found'}
              </p>
            </div>
          )}
          
          {/* Create new option */}
          {showCreateOption && (
            <button
              type="button"
              onClick={openCreateWithSearch}
              className="w-full px-4 py-3 text-left hover:bg-primary/10 flex items-center gap-3 border-t bg-muted/30"
            >
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <p className="font-medium text-primary">
                  {language === 'fr' ? 'Créer une nouvelle personne' : 'Create new person'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {searchTerm && `"${searchTerm}"`}
                </p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Create person dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {language === 'fr' ? 'Nouvelle Personne' : 'New Person'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={newPerson.first_name}
                  onChange={(e) => setNewPerson({ ...newPerson, first_name: e.target.value })}
                  placeholder="Jean"
                  data-testid="new-person-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={newPerson.last_name}
                  onChange={(e) => setNewPerson({ ...newPerson, last_name: e.target.value })}
                  placeholder="Kabila"
                  data-testid="new-person-lastname"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Identifiant National (UNJI)' : 'National ID (UNJI)'}</Label>
              <Input
                value={newPerson.national_id}
                onChange={(e) => setNewPerson({ ...newPerson, national_id: e.target.value })}
                placeholder="UNJI-123456789"
                data-testid="new-person-nationalid"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={newPerson.phone}
                onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
                placeholder="+243 XXX XXX XXX"
                data-testid="new-person-phone"
              />
            </div>

            <div className="space-y-2">
              <Label>Email ({language === 'fr' ? 'optionnel' : 'optional'})</Label>
              <Input
                type="email"
                value={newPerson.email}
                onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="new-person-email"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
              <Input
                value={newPerson.address}
                onChange={(e) => setNewPerson({ ...newPerson, address: e.target.value })}
                placeholder={language === 'fr' ? 'Commune, Ville' : 'District, City'}
                data-testid="new-person-address"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <input
                type="checkbox"
                id="create-account"
                checked={newPerson.create_account}
                onChange={(e) => setNewPerson({ ...newPerson, create_account: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="create-account" className="text-sm cursor-pointer">
                {language === 'fr' 
                  ? 'Créer un compte utilisateur (la personne pourra l\'activer plus tard)'
                  : 'Create user account (person can activate later)'}
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleCreatePerson} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Créer' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonSearchCombo;
